import { SettingOutlined } from "@ant-design/icons";
import { BackendService, ServicesResponseDTO } from "@dto/backendResponse.dto";
import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { MicroserviceChangeStateResponseDTO } from "@dto/microserviceResponse.dto";
import { MicroserviceVariablesResponse } from "@dto/microserviceVariables.dto";
import { UnitUtil } from "@dto/util/UnitUtil";
import { Switch, Tooltip } from "antd";
import { SwitchChangeEventHandler } from "antd/es/switch";
import axios, { AxiosError } from "axios";
import { FC, useContext, useEffect, useState } from "react";
import { FaCircle } from "react-icons/fa";
import { GridScope } from "src/component/grid/Grid";
import { IGridEntry } from "src/component/grid/gridTypes";
import { API_URL } from "src/data/Api";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";
import { ThemeUtil } from "src/util/ThemeUtil";
import { ApplicationContext } from "../../context/ApplicationContext";

export const API_URL_MICROS = {
  VARIABLES: "/api/configuration/v1/microservices/variables",
} as const;

export const ServicesTable: FC = () => {
  const {
    setOpenAdvancedModal,
    formAdvanced,
    setIsLoadingVariables,
    setMicroservice,
    setKeysOfValues,
    services,
    setServices,
    originalItemsRef
  } = useContext(ApplicationContext);

  const [gs] = useState(() => new GridScope<BackendService>());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get<ServicesResponseDTO>("/api/info/services")
      .then(
        (response) => {
          setServices(response.data.data.map((bs) => ({ ...bs, key: bs.microservice })));
        },
        (reason: AxiosError) => Log.error("Failed to get services!", reason)
      )
      .finally(() => setLoading(false));
  }, []);

  function switchState(record: BackendService & IGridEntry): SwitchChangeEventHandler {
    return (checked, event) => {
      const service = record.microservice;
      if (checked) {
        axios
          .patch<MicroserviceChangeStateResponseDTO>(`${API_URL.MICROSERVICES}/${service}/start`)
          .then(
            (response) => {
              setServices(prev => gs.updateRow({ ...response.data.data, key: record.key }, prev));
              Log.info(`Microservice ${service} started.`);
            },
            (error: AxiosError) => Log.error("Failed to start service: " + service, error)
          );
      } else {
        axios
          .patch<MicroserviceChangeStateResponseDTO>(`${API_URL.MICROSERVICES}/${service}/stop`)
          .then(
            (response) => {
              setServices(prev => gs.updateRow({ ...response.data.data, key: record.key }, prev));
              Log.info(`Microservice ${service} stopped.`);
            },
            (error: AxiosError) => Log.error("Failed to stop service: " + service, error)
          );
      }
    };
  }

  const openModal = async (record: BackendService) => {
    const { microservice } = record;
    setMicroservice(microservice);
    setKeysOfValues([]);
    setOpenAdvancedModal(true);
    try {
      setIsLoadingVariables(true);
      const responseVariables = await axios.get<MicroserviceVariablesResponse>(
        API_URL_MICROS.VARIABLES + `?hw_type=${microservice}`
      );
      const { microserviceVariables } = responseVariables.data.data;
      const filtered = microserviceVariables.filter((v) => v.microservice_variables_id != null);
      originalItemsRef.current = new Map(filtered.map((v) => [v.microservice_variables_id, { key: v.key, value: v.value }]));
      formAdvanced.setFieldsValue({
        items: filtered,
      });
    } catch (error) {
      const axiosError = error as AxiosError<BasicResponseDTO>;
      Log.error(axiosError.response?.data?.message ?? axiosError.message ?? "Failed to load variables");
    } finally {
      setIsLoadingVariables(false);
    }
  };

  return (
    <gs.Grid
      dataSource={services}
      loading={loading}
      rowKey='microservice'
      pagination={false}
    >
      <gs.Col
        key='microservice'
        title='Name'
        dataIndex='label'
        minWidth='10em'
        noTooltip
        sorter={(a, b) => GLOB.naturalSort(a.label, b.label)}
        render={(label, record) => !record.debug ? <Tooltip title={label}>{label}</Tooltip> : <span>{label} <Tooltip title="Service is running in debug mode."><FaCircle size={"8"} className={`xm-hs`} style={{ color: `${ThemeUtil.getHsColors()["warning"]}`, marginLeft: "0.5em" }} /></Tooltip></span>}
      />
      <gs.Col
        key='active'
        title='Active'
        dataIndex='active'
        width={50}
        render={(active, record) => (
          <div style={{ textAlign: "center" }}>
            <Switch size='small' checked={record.active} onChange={switchState(record)} />
          </div>
        )}
      ></gs.Col>
      <gs.Col
        key='script'
        title='Script'
        dataIndex='script'
        minWidth='15em'
        filter={{ field: ["script"] }}
        sorter={(l, r) => GLOB.naturalSort(l.script, r.script)}
      />
      <gs.Col
        key='monitor.cpu'
        title='CPU'
        dataIndex={["monitor", "cpu_percent"]}
        width={55}
        sorter={(a, b) => a.monitor?.cpu_percent - b.monitor?.cpu_percent}
        render={(value) => value + " %"}
      />
      <gs.Col
        key='monitor.memory'
        title='Memory'
        dataIndex={["monitor", "memory_byte"]}
        minWidth='6em'
        sorter={(l, r) => l.monitor?.memory_byte - r.monitor?.memory_byte}
        render={(value: number) => UnitUtil.formatSI(value)}
      />
      <gs.Col
        title='Advanced'
        minWidth='6em'
        align='center'
        render={(_, record) => (
          <a onClick={() => openModal(record)}>
            <SettingOutlined style={{ fontSize: "14px" }} />
          </a>
        )}
      />
    </gs.Grid>
  );
};
